import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import { ArrowRight } from "lucide-react";

/**
 * Shared shell for concept/roadmap product pages: /node, /os, /labs/mpmp
 */

export function ConceptPage({
  state,
  eyebrow,
  title,
  subtitle,
  intro,
  sections,
  cta,
  testid,
  handle,
  status,
}) {
  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
          <div className="flex items-center gap-3 flex-wrap">
            {state.map((s) => (
              <StateBadge key={s} state={s} />
            ))}
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">{eyebrow}</span>
          </div>
          <h1 data-testid={`${testid}-title`} className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            {title}
          </h1>
          {subtitle && <div className="mt-2 text-white/40 text-2xl md:text-3xl font-display font-extrabold">{subtitle}</div>}
          <p className="mt-6 max-w-2xl text-white/60 text-lg">{intro}</p>
          {status && (
            <div className="mt-8 inline-flex gap-2 font-mono text-[10px]">
              {status.map((s) => (
                <span key={s.label} className={`px-2 py-1 border ${s.color}`}>● {s.label}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {sections.map((sec, i) => (
        <section key={i} className={`border-b border-white/10 ${i % 2 === 1 ? "bg-[#0d0c0b]" : ""}`}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">{sec.eyebrow}</div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl max-w-3xl mb-10">
              {sec.title}
            </h2>
            {sec.body && (
              <p className="text-white/60 max-w-2xl leading-relaxed mb-10">{sec.body}</p>
            )}
            {sec.items && (
              <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {sec.items.map((item, j) => (
                  <div key={j} className="bg-[#0A0908] p-6 min-h-[160px]">
                    {item.num && (
                      <div className="font-mono text-xs text-[#E8D6A3] mb-3">{item.num}</div>
                    )}
                    <div className="font-display font-extrabold text-lg mb-2">{item.title}</div>
                    <div className="text-white/60 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                ))}
              </div>
            )}
            {sec.mono && (
              <div className="p-6 border border-[#E8D6A3]/20 bg-white/[0.02] font-mono text-sm text-white/70 leading-loose">
                {sec.mono}
              </div>
            )}
          </div>
        </section>
      ))}

      {handle && (
        <section className="border-b border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">Hypothetical endpoint</div>
            <div className="p-6 border border-[#E8D6A3]/30 bg-black font-mono text-lg break-all" data-testid={`${testid}-handle`}>
              {handle}
            </div>
            <div className="mt-3 text-xs text-white/40">Illustrative address format — not yet standardised.</div>
          </div>
        </section>
      )}

      {cta && (
        <section>
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
              {cta.title}
            </h2>
            <Link to={cta.to} className="mt-8 inline-flex items-center gap-2 px-6 py-4 bg-white text-black font-bold">
              {cta.label} <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}
    </PublicShell>
  );
}

/* ---------- /node ---------- */
export function NodePage() {
  return (
    <ConceptPage
      state={["BETA", "ROADMAP"]}
      eyebrow="Mirrorloops Node"
      title="The bridge between a screen and the network."
      intro="The Node is the software (and eventually hardware) that turns any display into a Mirrorloops endpoint — authenticate, receive, cache, execute, verify, report."
      status={[
        { label: "ONLINE", color: "border-emerald-500/40 text-emerald-400" },
        { label: "OFFLINE", color: "border-white/20 text-white/50" },
        { label: "DEGRADED", color: "border-[#D4B878]/40 text-[#E8D6A3]" },
        { label: "MAINTENANCE", color: "border-[#00E5FF]/40 text-[#00E5FF]" },
      ]}
      testid="node"
      sections={[
        {
          eyebrow: "What Node does",
          title: "One process. Nine responsibilities.",
          items: [
            { num: "01", title: "Connect screen", desc: "Register capabilities: resolution, orientation, hours, geolocation." },
            { num: "02", title: "Authenticate", desc: "Signed handshake against the Mirrorloops network." },
            { num: "03", title: "Receive campaigns", desc: "Pull creative + schedules on a heartbeat." },
            { num: "04", title: "Cache creative", desc: "Local storage so playback survives connectivity drops." },
            { num: "05", title: "Execute rules", desc: "Fire schedules and programmable rules from `/programmable`." },
            { num: "06", title: "Report playback", desc: "Proof-of-play events, per second." },
            { num: "07", title: "Monitor health", desc: "Temperature, CPU, memory, playback anomalies." },
            { num: "08", title: "Send telemetry", desc: "Aggregated context signals — never surveillance." },
            { num: "09", title: "Fail gracefully", desc: "Offline → cached loop → resync on reconnect." },
          ],
        },
        {
          eyebrow: "Available today",
          title: "Software only — for now.",
          body:
            "The browser-based Signage Player is the shipping Node runtime today. Purpose-built hardware devices (Node Micro, Node Rack, Node Board) are on the roadmap and clearly labeled as such. We will not claim hardware that does not exist.",
        },
      ]}
      cta={{ title: "Turn any screen into a Node.", label: "Get the player", to: "/app/signage" }}
    />
  );
}

/* ---------- /os ---------- */
export function OSPage() {
  return (
    <ConceptPage
      state={["BETA"]}
      eyebrow="Mirrorloops OS"
      title="An operating layer for connected physical media."
      intro="Cloud → API → OS → Node → Endpoint → Context → Public Environment. Mirrorloops OS is the runtime that binds all of this into a coherent, permissioned system."
      testid="os"
      sections={[
        {
          eyebrow: "Conceptual architecture",
          title: "Nine capabilities. One system.",
          items: [
            { num: "01", title: "Device management", desc: "Screens, Nodes, peripherals." },
            { num: "02", title: "Campaign scheduling", desc: "Time, place, rules, permissions." },
            { num: "03", title: "Creative delivery", desc: "CDN + edge caching." },
            { num: "04", title: "Rules engine", desc: "Executes programmable ad logic on-device." },
            { num: "05", title: "Health monitoring", desc: "Screen + Node telemetry." },
            { num: "06", title: "Proof of Play", desc: "Signed playback receipts." },
            { num: "07", title: "Permissions", desc: "Venue policies, category filters, brand safety." },
            { num: "08", title: "Security", desc: "Per-endpoint keys, replay protection." },
            { num: "09", title: "APIs", desc: "Open APIs for buyers, sellers, agencies." },
          ],
        },
        {
          eyebrow: "Flow",
          title: "Cloud → API → OS → Node → Endpoint → Context → Environment.",
          mono: "mirrorloops.cloud → api.mirrorloops.com → os.mirrorloops.local → node.mirrorloops.local → screen://venue-014/03 → context://weather.rain,time.morning → environment://mumbai/bandra",
        },
      ]}
      cta={{ title: "Explore the network built on OS.", label: "Open Network Explorer", to: "/network" }}
    />
  );
}

/* ---------- /labs/mpmp ---------- */
export function MPMPPage() {
  return (
    <ConceptPage
      state={["LABS", "EXPERIMENTAL", "ROADMAP"]}
      eyebrow="Mirrorloops Labs · Physical Media Protocol"
      title="MPMP."
      subtitle="Mirrorloops Physical Media Protocol"
      intro="A speculative R&D initiative to give every physical media surface a common, permissioned, machine-readable identity. Not an industry standard — yet. Public draft."
      testid="mpmp"
      handle="mirrorloops://india/mumbai/bandra/venue-014/screen-03"
      sections={[
        {
          eyebrow: "Design goals",
          title: "Make physical media surfaces addressable.",
          items: [
            { title: "Addressable", desc: "Every endpoint has a globally unique handle." },
            { title: "Discoverable", desc: "Browsable by geography, venue, environment, context." },
            { title: "Machine-readable", desc: "Standard schema for capabilities and availability." },
            { title: "Permissioned", desc: "Venues and owners set what may play on their surfaces." },
            { title: "Programmable", desc: "Endpoints publish which rule inputs they support." },
            { title: "Measurable", desc: "Standard proof-of-play format." },
            { title: "Verifiable", desc: "Cryptographic signatures on delivery receipts." },
          ],
        },
        {
          eyebrow: "What an endpoint should describe",
          title: "Seven facts, always.",
          mono:
            "WHO operates it  ·  WHERE it exists  ·  WHAT it can display  ·  WHEN it is available  ·  HOW it can be activated  ·  WHAT rules apply  ·  HOW delivery is verified",
        },
        {
          eyebrow: "Status",
          title: "This is Mirrorloops Labs R&D.",
          body:
            "MPMP is a public draft, not yet a working implementation. We will publish specs progressively as the OS matures. If you want to co-author or review, get in touch.",
        },
      ]}
      cta={{ title: "Get involved.", label: "Contact Mirrorloops Labs", to: "mailto:labs@mirrorloops.com" }}
    />
  );
}
